<!--
  ClientList — orders of clients (redesigned 2026-06-04).

  Route `/clientorders?token=…` — standalone only (no embedded use
  today). Visual language mirrors AgenteList: sticky header, modern
  table, row expand, drawers for the two preserved child widgets.
-->

<div class="orders-page">

  <!-- ─── Sticky header ────────────────────────────────────────── -->
  <header class="orders-header">
    <div class="orders-header__titles">
      <h1 class="orders-header__title">📦 Ordini clienti</h1>
      <p class="orders-header__subtitle">
        @if (hasOrders) {
          {{ filteredOrders.length }} di {{ orders.length }} ordini
          @if (partialCount > 0) {
            · <span class="orders-header__partial">{{ partialCount }} parziali</span>
          }
        } @else if (loading) {
          Caricamento…
        } @else {
          Nessun ordine cliente trovato
        }
      </p>
    </div>

    <div class="orders-header__controls">
      <div class="search-wrap">
        <input
          type="text"
          class="search-input"
          placeholder="Cerca per n° ordine, cliente, agente…"
          [(ngModel)]="searchText"
          (ngModelChange)="onSearchInput()"
          aria-label="Cerca ordine"
        />
        @if (searchText) {
          <button
            type="button"
            class="search-clear"
            (click)="clearSearch()"
            aria-label="Cancella ricerca"
            title="Cancella ricerca"
          >×</button>
        }
      </div>
    </div>
  </header>

  <!-- ─── Table card ────────────────────────────────────────────── -->
  <section class="orders-table-card" aria-label="Elenco ordini clienti">

    @if (loading && !hasOrders) {
      <div class="table-skeleton" aria-busy="true">
        @for (i of [1, 2, 3, 4, 5, 6, 7, 8]; track i) {
          <div class="table-skeleton__row">
            <div class="table-skeleton__line table-skeleton__line--lg"></div>
            <div class="table-skeleton__line table-skeleton__line--md"></div>
            <div class="table-skeleton__line table-skeleton__line--sm"></div>
          </div>
        }
      </div>
    } @else if (filteredOrders.length === 0) {
      <div class="orders-empty">
        <div class="orders-empty__icon" aria-hidden="true">
          @if (!hasOrders) { 📭 } @else { 🔎 }
        </div>
        <p class="orders-empty__msg">
          @if (!hasOrders) {
            Nessun ordine cliente disponibile.
          } @else if (searchText) {
            Nessun ordine corrisponde a "<strong>{{ searchText }}</strong>".
          } @else {
            Nessun risultato.
          }
        </p>
      </div>
    } @else {
      <table class="orders-table" role="table">
        <thead>
          <tr>
            <th class="col-order">Ordine</th>
            <th class="col-customer">Cliente</th>
            <th class="col-agent">Agente</th>
            <th class="col-date">Data</th>
            <th class="col-priority">Priorità</th>
            <th class="col-status">Avanzamento</th>
            <th class="col-actions" aria-label="Azioni"></th>
          </tr>
        </thead>
        <tbody>
          @for (order of pagedOrders; track order.order_id) {
            <tr
              class="orders-row"
              [attr.data-priority]="priorityVariant(order)"
              (click)="openOrderDetails(order)"
              title="Apri dettagli ordine"
            >
              <td class="col-order">
                <span class="cell-order-num">{{ order.or_order_number }}</span>
                @if (isPartial(order)) {
                  <span class="cell-partial-pill" title="Ordine parziale">P</span>
                }
                @if (order.or_quote_number) {
                  <span class="cell-quote">Preventivo {{ order.or_quote_number }}</span>
                }
              </td>
              <td class="col-customer">
                <span class="cell-customer">{{ order.cl_client || order.or_customer || '—' }}</span>
                @if (order.or_client_code) {
                  <span class="cell-code">Codice {{ order.or_client_code }}</span>
                }
              </td>
              <td class="col-agent">
                @if (order.ag_first_name) {
                  <span class="cell-agent">👤 {{ order.ag_first_name }}</span>
                } @else {
                  <span class="cell-muted">—</span>
                }
              </td>
              <td class="col-date">
                <span class="cell-date">{{ formatDate(order.or_order_date) }}</span>
                @if (order.or_closing_date && !order.or_closing_date.startsWith('0001-01-01')) {
                  <span class="cell-closed">Chiuso {{ formatDate(order.or_closing_date) }}</span>
                }
              </td>
              <td class="col-priority">
                <span class="priority-badge" [attr.data-variant]="priorityVariant(order)">
                  {{ order.tab_order_priority_desc || '—' }}
                </span>
              </td>
              <td class="col-status">
                <div class="status-cell">
                  <div class="status-cell__bar">
                    <div
                      class="status-cell__fill"
                      [style.width.%]="progress(order)"
                      [class.status-cell__fill--complete]="progress(order) >= 100"
                    ></div>
                  </div>
                  <div class="status-cell__meta">
                    <span class="status-cell__desc">{{ order.tab_order_status_desc || '—' }}</span>
                    <span class="status-cell__pct">{{ progress(order) }}%</span>
                  </div>
                </div>
              </td>
              <td class="col-actions" (click)="$event.stopPropagation()">
                <div class="row-actions">
                  <button
                    type="button"
                    class="icon-btn icon-btn--details"
                    title="Dettagli ordine"
                    (click)="openOrderDetails(order)"
                  >📦</button>
                  @if (order.os_tracking_number) {
                    <button
                      type="button"
                      class="icon-btn icon-btn--tracking"
                      title="Tracking spedizione"
                      (click)="openTracking(order)"
                    >📍</button>
                  }
                  @if (order.or_url_ddt) {
                    <a
                      class="icon-btn icon-btn--ddt"
                      [attr.href]="order.or_url_ddt"
                      target="_blank"
                      rel="noopener"
                      title="Apri DDT"
                    >📄</a>
                  }
                </div>
              </td>
            </tr>

          }
        </tbody>
      </table>

      @if (totalPages > 1) {
        <nav class="pagination" aria-label="Paginazione ordini">
          <button
            type="button"
            class="pagination__btn"
            [disabled]="currentPage === 0"
            (click)="prevPage()"
          >‹ Precedente</button>
          <span class="pagination__info">
            Pagina <strong>{{ currentPage + 1 }}</strong> di {{ totalPages }}
            · <span class="pagination__total">{{ filteredOrders.length }} ordini</span>
          </span>
          <button
            type="button"
            class="pagination__btn"
            [disabled]="currentPage >= totalPages - 1"
            (click)="nextPage()"
          >Successiva ›</button>
        </nav>
      }
    }
  </section>

  <!-- ─── Order details drawer ─────────────────────────────────── -->
  @if (showOrderDetails) {
    <div class="orders-drawer-backdrop" (click)="closeOrderDetails()">
      <aside class="orders-drawer" (click)="$event.stopPropagation()">
        <header class="orders-drawer__header">
          <div class="orders-drawer__title-block">
            <h2 class="orders-drawer__title">📦 Dettagli ordine {{ detailsOrderNumber || detailsOrderId }}</h2>
          </div>
          <button type="button" class="modal-close" (click)="closeOrderDetails()" aria-label="Chiudi">×</button>
        </header>
        <div class="orders-drawer__body">
          <app-ordini-agente-dettaggli
            [openOrderDettagli]="true"
            [agent_id]="detailsAgentId"
            [order_id]="detailsOrderId"
            [token_agent]="detailsTokenAgent"
            [or_assigned_date]="detailsAssignedDate"
            [status_cod]="detailsStatusCod"
          ></app-ordini-agente-dettaggli>
        </div>
      </aside>
    </div>
  }

  <!-- ─── Tracking drawer ──────────────────────────────────────── -->
  @if (showTracking) {
    <div class="orders-drawer-backdrop" (click)="closeTracking()">
      <aside class="orders-drawer orders-drawer--tracking" (click)="$event.stopPropagation()">
        <header class="orders-drawer__header">
          <div class="orders-drawer__title-block">
            <h2 class="orders-drawer__title">📍 Tracking spedizione</h2>
          </div>
          <button type="button" class="modal-close" (click)="closeTracking()" aria-label="Chiudi">×</button>
        </header>
        <div class="orders-drawer__body">
          <app-accesso-funzionari-tracking
            [track_ID]="trackingTrackId"
            [token]="client_token"
            [newTrack]="true"
          ></app-accesso-funzionari-tracking>
        </div>
      </aside>
    </div>
  }

</div>
